import React, { useEffect, useState } from "react";
import { useAppDispatch } from "@redux/hooks";
import { tagApi } from "@api/tags";
import "./Tags.css";
import { useLocation } from "react-router-dom";
import { useUser } from "@hooks/useUser";
import { usePreload } from "@hooks/usePreload";
import { fetchPreload } from "@redux/slices/preloadSlice";
import { TagType } from "types";
import PageHeader from "./PageHeader";
import ConfirmModal from "./ConfirmModal";

type TagsTextType = {
  [key: number]: {
    english?: string;
    japanese?: string;
  };
};

const Tags: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const adto = queryParams.get("adto");
  const lla = queryParams.get("lla"); // LiveLet admin page = 1

  const user = useUser();
  const { userKey, userToken } = user;
  const preload = usePreload();
  const existingTags = preload.tags;
  const dispatch = useAppDispatch();

  const [tagsText, setTagsText] = useState<TagsTextType>({});
  const [isAddingParent, setIsAddingParent] = useState(false);
  const [isAddingChild, setIsAddingChild] = useState<number | null>(null);
  const [newTag, setNewTag] = useState({ english: "", japanese: "", parentKey: null });
  // 削除の確認（null なら閉じている）と、削除できないときのお知らせ
  const [deleteTarget, setDeleteTarget] = useState<TagType | null>(null);
  const [cannotDeleteName, setCannotDeleteName] = useState<string | null>(null);

  useEffect(() => {
    if (existingTags) {
      const initialTags: TagsTextType = {};
      existingTags.forEach((tag: TagType) => {
        initialTags[tag.tagKey] = {
          english: tag.english || "",
          japanese: tag.japanese || "",
        };
      });
      setTagsText(initialTags);
    }
  }, [existingTags]);

  const handleUpdate = async (tagKey: number) => {
    if (!userKey || !userToken) return;
    try {
      const languageValues = { ...tagsText[tagKey] };
      await tagApi.editTag(userKey, userToken, tagKey, languageValues);
      dispatch(fetchPreload());
    } catch (error) {
      console.error("Failed to update tag:", error);
    }
  };

  // 削除ボタン: 子のタグ（オプション）があれば消せない旨を、なければ確認を出す
  const requestDelete = (tag: TagType) => {
    const hasChildren = existingTags && existingTags.some((t: TagType) => t.parentKey === tag.tagKey);
    if (hasChildren) {
      setCannotDeleteName(tagsText[tag.tagKey]?.japanese || tag.japanese || "");
      return;
    }
    setDeleteTarget(tag);
  };

  const handleDelete = async (tagKey: number) => {
    if (!userKey || !userToken) return;
    try {
      await tagApi.deleteTag(tagKey, userKey, userToken);
      dispatch(fetchPreload());
    } catch (error) {
      console.error("Failed to delete tag:", error);
    }
  };

  const handleCreate = async () => {
    if (!userKey || !userToken) return;
    try {
      const parentKey = isAddingChild || null;
      const languageValues = { english: newTag.english, japanese: newTag.japanese };
      await tagApi.saveTag(userKey, userToken, { ...languageValues }, parentKey);
      setIsAddingParent(false);
      setIsAddingChild(null);
      setNewTag({ english: "", japanese: "", parentKey: null });
      dispatch(fetchPreload());
    } catch (error) {
      console.error("Failed to create tag:", error);
    }
  };

  const parentTags = existingTags && existingTags.filter((tag: TagType) => !tag.parentKey) || [];
  const getChildren = (parentKey: number) => existingTags && existingTags.filter((tag: TagType) => tag.parentKey === parentKey) || [];

  const plusIcon = <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M6 1v10M1 6h10" fill="none" stroke="currentColor" strokeWidth="2" /></svg>;

  // タグ名の入力欄と、更新・削除ボタンの1行
  const tagRow = (tag: TagType, isChild: boolean) => (
    <div key={tag.tagKey} className={`tagRow${isChild ? " is-child" : ""}`}>
      <input
        type="text"
        aria-label="タグ名"
        value={tagsText[tag.tagKey]?.japanese || ""}
        onChange={(e) => setTagsText({ ...tagsText, [tag.tagKey]: { ...tagsText[tag.tagKey], japanese: e.target.value } })}
        className="c-input tagRow__input"
      />
      <div className="tagRow__actions">
        <button type="button" onClick={() => handleUpdate(tag.tagKey)} className="btn btn-secondary">更新</button>
        <button type="button" onClick={() => requestDelete(tag)} className="btn btn-danger">削除</button>
      </div>
    </div>
  );

  // 新しいタグ・オプションの入力欄
  const newTagForm = (onClose: () => void, isChild: boolean) => (
    <div className={`tagRow${isChild ? " is-child" : ""}`}>
      <input type="text" aria-label="新しいタグ名" placeholder="タグ名を入力" value={newTag.japanese} onChange={(e) => setNewTag({ ...newTag, japanese: e.target.value })} className="c-input tagRow__input" />
      <div className="tagRow__actions">
        <button type="button" onClick={handleCreate} className="btn btn-primary">保存</button>
        <button type="button" onClick={onClose} className="btn btn-secondary">閉じる</button>
      </div>
    </div>
  );

  return (
    <div className="top-page-container">
      <PageHeader title="タグの管理" showBack={lla !== "1"} />
      <div className="pageBody tagBoxes">
        {/* タグの種類（共通タグ・業種タグ…）ごとに、見出し帯つきの箱にする */}
        {parentTags.map((parent) => (
          <section key={parent.tagKey} className="drop-shadow-md">
            <div className="bg-primary-gray rounded-t-md">
              <h2 className="c-box__title">{parent.japanese || parent.name}</h2>
            </div>
            <div className="bg-white rounded-b-md c-box__body tagBox__body">
              {tagRow(parent, false)}
              {getChildren(parent.tagKey).map((child) => tagRow(child, true))}
              {isAddingChild === parent.tagKey ? (
                newTagForm(() => setIsAddingChild(null), true)
              ) : (
                <div className="tagRow is-child">
                  <button type="button" onClick={() => {setIsAddingParent(false); setIsAddingChild(parent.tagKey)}} className="btn btn-secondary tagAddButton">{plusIcon}オプションを追加</button>
                </div>
              )}
            </div>
          </section>
        ))}
        {isAddingParent ? (
          <section className="drop-shadow-md">
            <div className="bg-primary-gray rounded-t-md">
              <h2 className="c-box__title">新しいタグ</h2>
            </div>
            <div className="bg-white rounded-b-md c-box__body">
              {newTagForm(() => setIsAddingParent(false), false)}
            </div>
          </section>
        ) : (
          <div>
            <button type="button" onClick={() => {setIsAddingChild(null); setIsAddingParent(true)}} className="btn btn-primary tagAddButton">{plusIcon}タグを追加</button>
          </div>
        )}
      </div>
      <ConfirmModal
        open={deleteTarget !== null}
        title="タグを削除しますか？"
        message={<>
          <p>「{deleteTarget ? (tagsText[deleteTarget.tagKey]?.japanese || deleteTarget.japanese || "") : ""}」を削除します。</p>
          <p>この操作は取り消せません。</p>
        </>}
        confirmLabel="削除する"
        danger
        onConfirm={() => { const target = deleteTarget; setDeleteTarget(null); if (target) handleDelete(target.tagKey); }}
        onCancel={() => setDeleteTarget(null)}
      />
      <ConfirmModal
        open={cannotDeleteName !== null}
        title="このタグは削除できません"
        message={<p>「{cannotDeleteName}」にはオプションがあります。先にオプションをすべて削除してください。</p>}
        confirmLabel="閉じる"
        hideCancel
        onConfirm={() => setCannotDeleteName(null)}
        onCancel={() => setCannotDeleteName(null)}
      />
    </div>
  );
};

export default Tags;
